import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Layers } from "lucide-react";
import { toast } from "sonner";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const iconBtn =
    "size-8 text-[#8A8178] hover:bg-[#FAF9F6] hover:text-[#3D2817]";

const deleteBtn =
    "size-8 text-[#B8859C] hover:bg-[#FDE8EC] hover:text-[#4A0E17]";

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

export default function Services() {
    const [services, setServices] = useState([]);
    const [cats, setCats] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        const [s, c] = await Promise.all([api.get("/services"), api.get("/categories")]);
        setServices(s.data);
        setCats(c.data);
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await api.delete(`/services/${id}`);
        toast.success("Deleted");
        load();
    };

    const grouped = cats.map((c) => ({
        cat: c,
        items: services.filter((s) => s.category_id === c.id),
    }));
    const orphans = services.filter((s) => !cats.find((c) => c.id === s.category_id));
    const activeGroups = [...grouped, { cat: { id: "_other", name: "Other", color: "#9A9088" }, items: orphans }]
        .filter((g) => g.items.length);

    const categoryCount = activeGroups.length;

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="services-page">
            <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className={EYEBROW} style={{ color: GOLD }}>
                        Catalogue
                    </p>
                    <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                        Services
                    </h1>
                    <GoldDivider className="mt-3 max-w-[200px]" />
                    <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#8A8178]">
                        <Layers className="size-4 text-[#D4AF37]" />
                        {services.length} service{services.length !== 1 ? "s" : ""}
                        {categoryCount > 0 && (
                            <>
                                <span className="text-[#E8E4DE]">|</span>
                                <span>{categoryCount} categor{categoryCount !== 1 ? "ies" : "y"}</span>
                            </>
                        )}
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]"
                            onClick={() => setEditing(null)}
                            data-testid="add-service-button"
                        >
                            <Plus className="mr-1 size-4" /> Add service
                        </Button>
                    </DialogTrigger>
                    <ServiceDialog
                        cats={cats}
                        editing={editing}
                        onClose={() => setOpen(false)}
                        onSaved={() => {
                            load();
                            setOpen(false);
                        }}
                    />
                </Dialog>
            </header>

            {services.length === 0 ? (
                <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white px-6 py-14 text-center shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                    <p className="font-serif text-lg font-bold" style={{ color: CHOCOLATE }}>No services yet</p>
                    <p className="mt-2 text-sm font-medium text-[#8A8178]">Add your first service to build the catalogue.</p>
                </div>
            ) : (
                <div className="space-y-5">
                    {activeGroups.map(({ cat, items }) => (
                        <div
                            key={cat.id}
                            className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)]"
                        >
                            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                            <div className="flex items-center gap-3 border-b border-[#E8E4DE] bg-gradient-to-r from-[#FFFEF9] to-[#FAF9F6] px-5 py-3.5">
                                <span
                                    className="size-2.5 shrink-0 rounded-full ring-2 ring-white"
                                    style={{ background: cat.color }}
                                />
                                <h2 className="font-serif text-lg font-bold" style={{ color: CHOCOLATE }}>
                                    {cat.name}
                                </h2>
                                <span className="text-xs font-semibold text-[#8A8178]">
                                    {items.length} service{items.length !== 1 ? "s" : ""}
                                </span>
                            </div>
                            <div className="divide-y divide-[#E8E4DE]/80">
                                {items.map((s) => (
                                    <div
                                        key={s.id}
                                        className="flex items-center justify-between px-5 py-3.5 transition-colors hover:bg-[#FAF9F6]/80"
                                        data-testid={`service-row-${s.id}`}
                                    >
                                        <div>
                                            <div className="font-semibold" style={{ color: CHOCOLATE }}>{s.name}</div>
                                            <div className="mt-0.5 text-xs font-medium text-[#8A8178]">
                                                {s.duration_min} min · {s.gst_rate}% GST
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="min-w-[4.5rem] text-right font-serif text-lg font-bold tabular-nums" style={{ color: CHOCOLATE }}>
                                                {inr(s.price)}
                                            </span>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className={iconBtn}
                                                onClick={() => {
                                                    setEditing(s);
                                                    setOpen(true);
                                                }}
                                                data-testid={`edit-service-${s.id}`}
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className={deleteBtn}
                                                onClick={() => remove(s.id)}
                                                data-testid={`delete-service-${s.id}`}
                                            >
                                                <Trash2 className="size-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ServiceDialog({ cats, editing, onClose, onSaved }) {
    const [f, setF] = useState({
        name: "", category_id: "", price: 0, duration_min: 30, gst_rate: 18, description: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (editing) {
            setF({
                name: editing.name, category_id: editing.category_id || "",
                price: editing.price, duration_min: editing.duration_min,
                gst_rate: editing.gst_rate || 18, description: editing.description || "",
            });
        } else {
            setF({ name: "", category_id: cats[0]?.id || "", price: 0, duration_min: 30, gst_rate: 18, description: "" });
        }
    }, [editing, cats]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f, price: Number(f.price), duration_min: Number(f.duration_min), gst_rate: Number(f.gst_rate) };
            if (editing) await api.put(`/services/${editing.id}`, payload);
            else await api.post("/services", payload);
            toast.success("Saved");
            onSaved();
        } catch {
            toast.error("Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="rounded-xl border-[#E8E4DE] p-6">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold" style={{ color: MAROON }}>
                    {editing ? "Edit service" : "Add service"}
                </DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Name</Label>
                    <Input className={fieldClass} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="svc-name" />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Category</Label>
                    <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
                        <SelectTrigger className={fieldClass} data-testid="svc-cat"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                            {cats.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Price (₹)</Label>
                        <Input className={fieldClass} type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="svc-price" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Duration (m)</Label>
                        <Input className={fieldClass} type="number" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: e.target.value })} data-testid="svc-duration" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>GST %</Label>
                        <Input className={fieldClass} type="number" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} data-testid="svc-gst" />
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button type="submit" className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]" disabled={busy} data-testid="save-service-button">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
